import PaymentV2API from '@/api/v2/payment'
import PaymentTypes from '@/constants/payment-types'
import Storage from '@/utils/storage'
import clientLogger from '@/plugins/logger/core'
import { getABTProvider } from '@/plugins/abt/statsig'

const abtProvider = getABTProvider()

const state = {
  list: [],
  metaData: {},
  fetched: false,
  fetching: false,
  isFailed3DS: false,
  isFailedTimeout: false,
  failRedirectionFrom3Ds: false,
  isCheckoutFailed: false,
  failedWithApple: false,
  fraudCheck: null,
}

const getters = {
  hasSavedCards(state) {
    return state.list && state.list.length > 0
  },
  isFraudCheckFailed(state) {
    return !!state.fraudCheck
  },
  isFailed3DS(state) {
    return state.isFailed3DS
  },
  isFailedTimeout(state) {
    return state.isFailedTimeout
  },
  isCheckoutFailed(state) {
    return state.isCheckoutFailed
  },
  failedWithApple(state) {
    return state.failedWithApple ?? false
  },
  applePayPreferenceOriginal(state) {
    return state.metaData?.applePayPreference ?? false
  },
  applePayPreference(state) {
    return state.metaData?.applePayPreference ?? true
  },
  hasMetaData(state) {
    return Object.keys(state?.metaData ?? {}).length > 0
  },
  hasSavedCardsNotBlacklisted(state) {
    const filteredCards = state.list?.filter(({ isBinBlacklisted }) => {
      return !isBinBlacklisted
    })

    // Has saved cards not in blacklisted
    return filteredCards.length > 0
  },
}

const mutations = {
  SET_CARD_LIST(state, list) {
    state.list = list
  },
  SET_META_DATA(state, metaData) {
    state.metaData = metaData
  },
  SET_FRAUD_CHECK(state, value) {
    state.fraudCheck = value
  },
  SET_STATUS(state, { key, value }) {
    state[key] = value
  },
}

const actions = {
  init({ dispatch }) {
    dispatch('setFailRedirectionFrom3Ds', Storage.getCko3dsStatus())
  },
  async getPaymentExistingCards({ commit, state, dispatch, rootGetters }) {
    if (state.fetched || state.fetching) {
      return
    }

    try {
      commit('SET_STATUS', { key: 'fetching', value: true })
      const paymentType = rootGetters['session/paymentType']
      const merchantId = rootGetters['session/merchantId']
      const orderId = rootGetters.orderId

      let applePayFixExperimentIsEnabled = true
      try {
        applePayFixExperimentIsEnabled =
          abtProvider.isFeatureEnabled('ce_1217_apple_pay_fix') ?? true
      } catch (err) {
        console.error(err)
      }

      const response = await PaymentV2API.getPaymentExistingCards(
        merchantId,
        paymentType,
        orderId
      )
      const { cards = [], metaData = {} } = response?.data ?? {}

      try {
        clientLogger.debug('Got cards. (v2 endpoint)', {
          metaData,
        })
      } catch (err) {
        console.error(err)
      }

      if (
        applePayFixExperimentIsEnabled &&
        rootGetters['instalment/payViaApplePay'] &&
        (metaData?.applePayPreference ?? null) === false
      ) {
        clientLogger.debug(
          'applePayPreference is FALSE - unselecting apple pay now'
        )

        await dispatch('instalment/unselectApplePay', null, {
          root: true,
        })
      }

      commit('SET_CARD_LIST', cards)
      commit('SET_META_DATA', metaData)
      commit('SET_STATUS', { key: 'fetched', value: true })
      commit('SET_STATUS', { key: 'fetching', value: false })

      if (TypeChecker.isArray(cards) && cards.length > 0) {
        if (paymentType === PaymentTypes.PAY_NEXT_MONTH) {
          await dispatch('instalment/setReadyToPay', true, {
            root: true,
          })
        } else {
          await dispatch('instalment/resetPaymentMethodStatus', null, {
            root: true,
          })
        }
      }

      return response
    } catch (error) {
      commit('SET_STATUS', { key: 'fetching', value: false })
      throw error
    }
  },
  allowGetExistingCards({ commit }) {
    commit('SET_STATUS', { key: 'fetched', value: false })
  },
  setFailed3DS({ commit }) {
    commit('SET_STATUS', { key: 'isFailed3DS', value: true })
  },
  setFailedTimeout({ commit }, value) {
    commit('SET_STATUS', { key: 'isFailedTimeout', value })
  },
  setFailRedirectionFrom3Ds({ commit }, status) {
    commit('SET_STATUS', { key: 'failRedirectionFrom3Ds', value: status })

    Storage.setCko3dsStatus(status)
  },
  setFraudCheck({ commit }, value) {
    commit('SET_FRAUD_CHECK', value)
  },
  setFailedWithApple({ commit }) {
    commit('SET_STATUS', { key: 'failedWithApple', value: true })
  },
  // Verify payment method was failed on Checkout.com
  setCheckoutFailed({ commit }) {
    commit('SET_STATUS', { key: 'isCheckoutFailed', value: true })
  },
  resetErrorToAllowAgain({ commit }) {
    commit('SET_FRAUD_CHECK', null)
    commit('SET_STATUS', { key: 'isFailed3DS', value: false })
    commit('SET_STATUS', { key: 'isFailedTimeout', value: false })
    commit('SET_STATUS', { key: 'isCheckoutFailed', value: false })
    commit('SET_STATUS', { key: 'failedWithApple', value: false })
  },
  clearSession({ commit, dispatch }) {
    commit('SET_CARD_LIST', [])
    commit('SET_META_DATA', {})
    commit('SET_STATUS', { key: 'fetching', value: false })
    commit('SET_STATUS', { key: 'fetched', value: false })

    dispatch('resetErrorToAllowAgain')
  },
}

export default { namespaced: true, state, getters, mutations, actions }
